import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Calculator } from "lucide-react";
import api from "@/lib/api";
import { PageHeader, Loader, EmptyState, Badge, fcTone, StatCard } from "@/components/kit";

export default function PCA() {
  const nav = useNavigate();
  const [menus, setMenus] = useState(null);
  useEffect(() => { api.get("/menus").then(async () => {
    const { data } = await api.get("/menus");
    const full = await Promise.all(data.menus.map((m) => api.get(`/menus/${m.id}`).then((r) => r.data.menu)));
    setMenus(full);
  }).catch(() => setMenus([])); }, []);
  if (!menus) return <Loader />;
  const dishes = menus.flatMap((m) => m.courses.map((c) => ({ ...c, menu: m })));
  const avg = dishes.length ? Math.round(dishes.reduce((s, d) => s + (d.pca?.food_cost_pct || 0), 0) / dishes.length * 10) / 10 : 0;
  const totalRev = menus.reduce((s, m) => s + (m.revenue_potential || 0), 0);
  const totalCost = menus.reduce((s, m) => s + (m.total_cost || 0), 0);

  return (
    <div>
      <PageHeader eyebrow="Food Cost" title="Plate Cost Analysis" subtitle="Reproducible costing across every dish. Prices are snapshotted when each PCA is generated." />
      {dishes.length === 0 ? <EmptyState icon={Calculator} title="No PCA yet" body="Generate a menu to see plate costs." /> : (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
            <StatCard label="Avg Food Cost" value={`${avg}%`} accent />
            <StatCard label="Revenue Potential" value={`$${totalRev.toLocaleString()}`} />
            <StatCard label="Ingredient Cost" value={`$${totalCost.toLocaleString()}`} />
            <StatCard label="Gross Profit" value={`$${(totalRev - totalCost).toLocaleString()}`} accent />
          </div>
          <div className="matte-card overflow-x-auto">
            <table className="w-full text-sm">
              <thead><tr className="text-left label-eyebrow border-b border-white/10">
                <th className="py-4 px-5">Dish</th><th className="py-4 px-3">Menu</th>
                <th className="py-4 px-3 text-right">Plate Cost</th><th className="py-4 px-3 text-right">Price</th>
                <th className="py-4 px-3 text-right">Profit</th><th className="py-4 px-5 text-right">Food Cost</th>
              </tr></thead>
              <tbody>
                {dishes.map((d, i) => (
                  <tr key={i} onClick={() => nav(`/menus/${d.menu.id}`)} data-testid={`pca-row-${i}`}
                    className="border-b border-white/5 hover:bg-black/40 cursor-pointer">
                    <td className="py-3.5 px-5"><p className="text-white/90">{d.name}</p><p className="text-white/40 text-xs">{d.course}</p></td>
                    <td className="py-3.5 px-3 text-white/50">{d.menu.menu_name}</td>
                    <td className="py-3.5 px-3 text-right font-mono text-white/70">${d.pca?.total_cost}</td>
                    <td className="py-3.5 px-3 text-right font-mono text-white/70">${d.pca?.selling_price}</td>
                    <td className="py-3.5 px-3 text-right font-mono text-gold">${d.pca?.gross_profit}</td>
                    <td className="py-3.5 px-5 text-right"><Badge tone={fcTone(d.pca?.food_cost_pct, d.menu.target_food_cost)}>{d.pca?.food_cost_pct}%</Badge></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}
